import { c as _c } from "react/compiler-runtime";
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { safeCall } from '@/app/utils/safeCall';
interface UseEntranceRevealOptions {
  hasEntranceReveal: boolean;
  hasCriticalSceneMedia: boolean;
  isCarCritical: boolean;
  onSceneReady?: () => void;
}
const decodeImage = async (image: HTMLImageElement) => {
  if (typeof image.decode !== 'function') return;
  try {
    await image.decode();
  } catch {
    // A failed decode must not leave the entrance sequence hidden.
  }
};
export const useEntranceReveal = t0 => {
  const $ = _c(37);
  const {
    hasEntranceReveal,
    hasCriticalSceneMedia,
    isCarCritical,
    onSceneReady
  } = t0;
  const [isSceneReady, setIsSceneReady] = useState(!hasEntranceReveal);
  const [isCarReady, setIsCarReady] = useState(!hasEntranceReveal);
  const criticalImageRef = useRef(null);
  const carImageRef = useRef(null);
  const sceneFrameRef = useRef(null);
  const carFrameRef = useRef(null);
  const hasScheduledSceneRef = useRef(!hasEntranceReveal);
  const hasScheduledCarRef = useRef(!hasEntranceReveal);
  const hasStartedSceneRef = useRef(!hasEntranceReveal);
  const isCarMediaReadyRef = useRef(!hasEntranceReveal);
  const isMountedRef = useRef(false);
  let t1;
  if ($[0] !== hasEntranceReveal) {
    t1 = () => {
      if (!hasEntranceReveal || !isMountedRef.current || hasScheduledCarRef.current) {
        return;
      }
      hasScheduledCarRef.current = true;
      carFrameRef.current = requestAnimationFrame(() => {
        carFrameRef.current = requestAnimationFrame(() => {
          setIsCarReady(true);
          carFrameRef.current = null;
        });
      });
    };
    $[0] = hasEntranceReveal;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scheduleCarReveal = t1;
  let t2;
  if ($[2] !== hasEntranceReveal || $[3] !== onSceneReady || $[4] !== scheduleCarReveal) {
    t2 = () => {
      if (!hasEntranceReveal || !isMountedRef.current || hasScheduledSceneRef.current) {
        return;
      }
      hasScheduledSceneRef.current = true;
      sceneFrameRef.current = requestAnimationFrame(() => {
        sceneFrameRef.current = requestAnimationFrame(() => {
          hasStartedSceneRef.current = true;
          setIsSceneReady(true);
          safeCall(onSceneReady);
          sceneFrameRef.current = null;
          if (isCarMediaReadyRef.current) {
            scheduleCarReveal();
          }
        });
      });
    };
    $[2] = hasEntranceReveal;
    $[3] = onSceneReady;
    $[4] = scheduleCarReveal;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const scheduleSceneReveal = t2;
  let t3;
  if ($[6] !== scheduleSceneReveal) {
    t3 = image => {
      decodeImage(image).then(scheduleSceneReveal);
    };
    $[6] = scheduleSceneReveal;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const revealSceneWhenReady = t3;
  let t4;
  if ($[8] !== isCarCritical || $[9] !== scheduleCarReveal || $[10] !== scheduleSceneReveal) {
    t4 = image_0 => {
      decodeImage(image_0).then(() => {
        isCarMediaReadyRef.current = true;
        if (isCarCritical) {
          scheduleSceneReveal();
        }
        if (hasStartedSceneRef.current) {
          scheduleCarReveal();
        }
      });
    };
    $[8] = isCarCritical;
    $[9] = scheduleCarReveal;
    $[10] = scheduleSceneReveal;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const revealCarWhenReady = t4;
  let t5;
  if ($[12] !== revealSceneWhenReady) {
    t5 = event => {
      revealSceneWhenReady(event.currentTarget);
    };
    $[12] = revealSceneWhenReady;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleCriticalMediaLoad = t5;
  let t6;
  if ($[14] !== scheduleSceneReveal) {
    t6 = () => {
      scheduleSceneReveal();
    };
    $[14] = scheduleSceneReveal;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleCriticalMediaError = t6;
  let t7;
  if ($[16] !== revealCarWhenReady) {
    t7 = event_0 => {
      revealCarWhenReady(event_0.currentTarget);
    };
    $[16] = revealCarWhenReady;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleCarMediaLoad = t7;
  let t8;
  if ($[18] !== isCarCritical || $[19] !== scheduleCarReveal || $[20] !== scheduleSceneReveal) {
    t8 = () => {
      isCarMediaReadyRef.current = true;
      if (isCarCritical) {
        scheduleSceneReveal();
      }
      if (hasStartedSceneRef.current) {
        scheduleCarReveal();
      }
    };
    $[18] = isCarCritical;
    $[19] = scheduleCarReveal;
    $[20] = scheduleSceneReveal;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleCarMediaError = t8;
  let t10;
  let t9;
  if ($[22] !== hasCriticalSceneMedia || $[23] !== hasEntranceReveal || $[24] !== isCarCritical || $[25] !== revealCarWhenReady || $[26] !== revealSceneWhenReady || $[27] !== scheduleSceneReveal) {
    t9 = () => {
      isMountedRef.current = true;
      const criticalImage = isCarCritical ? carImageRef.current : criticalImageRef.current;
      const carImage = carImageRef.current;
      if (hasEntranceReveal && carImage?.complete) {
        revealCarWhenReady(carImage);
      }
      if (hasEntranceReveal && hasCriticalSceneMedia && criticalImage?.complete && !isCarCritical) {
        revealSceneWhenReady(criticalImage);
      } else {
        if (hasEntranceReveal && !hasCriticalSceneMedia) {
          scheduleSceneReveal();
        }
      }
      return () => {
        isMountedRef.current = false;
        if (sceneFrameRef.current !== null) {
          cancelAnimationFrame(sceneFrameRef.current);
          sceneFrameRef.current = null;
          hasScheduledSceneRef.current = false;
        }
        if (carFrameRef.current !== null) {
          cancelAnimationFrame(carFrameRef.current);
          carFrameRef.current = null;
          hasScheduledCarRef.current = false;
        }
      };
    };
    t10 = [hasCriticalSceneMedia, hasEntranceReveal, isCarCritical, revealCarWhenReady, revealSceneWhenReady, scheduleSceneReveal];
    $[22] = hasCriticalSceneMedia;
    $[23] = hasEntranceReveal;
    $[24] = isCarCritical;
    $[25] = revealCarWhenReady;
    $[26] = revealSceneWhenReady;
    $[27] = scheduleSceneReveal;
    $[28] = t10;
    $[29] = t9;
  } else {
    t10 = $[28];
    t9 = $[29];
  }
  useEffect(t9, t10);
  let t11;
  if ($[30] !== handleCarMediaError || $[31] !== handleCarMediaLoad || $[32] !== handleCriticalMediaError || $[33] !== handleCriticalMediaLoad || $[34] !== isCarReady || $[35] !== isSceneReady) {
    t11 = {
      carImageRef,
      criticalImageRef,
      handleCarMediaError,
      handleCarMediaLoad,
      handleCriticalMediaError,
      handleCriticalMediaLoad,
      isCarReady,
      isSceneReady
    };
    $[30] = handleCarMediaError;
    $[31] = handleCarMediaLoad;
    $[32] = handleCriticalMediaError;
    $[33] = handleCriticalMediaLoad;
    $[34] = isCarReady;
    $[35] = isSceneReady;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
};